import { CircleButton } from "components/CircleButton";

import { Box, Drawer, styled, Typography, useTheme } from "@mui/material";
import { MAX_SLIPPAGE, MAX_VALIDATOR_TIP, MIN_VALIDATOR_TIP } from "consts";
import { MIN_SLIPPAGE } from "consts";
import { Close } from "@mui/icons-material";
import { DarkDrawer } from "components/TradeDrawer";
import { StyledInputV2 } from "components/CrossChainSwapDrawerV2/styled";
import { RetroButton } from "components/RetroButton";
import { SLIPPAGE_OPTIONS, VALIDATOR_TIP_OPTIONS } from "../../utils/configs";
import { Col, Row } from "v2/components/base/Flex/ui";
import CloseButton from "v2/components/Buttons/CloseButton";
import { useRef } from "react";
import { useEffect } from "react";
import { TransparentInputWithUnit } from "v2/components/base/Input/ui";
import BasicButton from "v2/components/Buttons/BasicButton";

const PreferencesDrawer = ({
  isOpen,
  close,
  slippageRef,
  handleSlippageChange,
  slippage,
  showValidatorTip,
  validatorTipRef,
  handleValidatorTipChange,
  validatorTip,
  isShowTips,
  isSlippageValid,
  handleConfirmPreferences,
}: {
  isOpen: boolean;
  close: () => void;
  slippageRef: React.RefObject<HTMLInputElement>;
  handleSlippageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  slippage: number;
  showValidatorTip: boolean;
  validatorTipRef: React.RefObject<HTMLInputElement>;
  handleValidatorTipChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validatorTip: number;
  isShowTips: boolean;
  isSlippageValid: boolean;
  handleConfirmPreferences: () => void;
}) => {
  const theme = useTheme();
  return (
    <Drawer
      anchor={"bottom"}
      open={isOpen}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "1200px",
          backgroundColor: theme.palette.surface["container-bg"],
          color: theme.palette.text.primary,
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          marginX: "auto",
          borderRadius: "16px 16px 0 0",
        },
      }}>
      <Col type="sc" sx={{ padding: "16px" }} fullWidth>
        {/* Slippage */}
        <Row type="ec" fullWidth>
          <CloseButton onClick={close} />
        </Row>
        <Row sx={{ marginTop: "32px" }} />
        <Row type="bc" fullWidth>
          <Typography variant="mobile-body-m-heavy">Slippage</Typography>
        </Row>
        <Row sx={{ marginTop: "8px" }} fullWidth>
          <Row sx={{ position: "relative" }} fullWidth>
            <TransparentInputWithUnit
              type="number"
              min={MIN_SLIPPAGE}
              max={MAX_SLIPPAGE}
              step="0.1"
              amount={slippage}
              amountRef={slippageRef}
              onInputChange={handleSlippageChange}
              defaultValue={slippage}
              unit="%"
            />
          </Row>
        </Row>
        <Row type="sc" sx={{ gap: "8px", marginTop: "8px" }} fullWidth>
          {SLIPPAGE_OPTIONS.map((option) => (
            <OptionButton
              key={option.value}
              label={option.label}
              onClick={() => {
                slippageRef.current!.value = option.value.toString();
                handleSlippageChange({
                  target: { value: option.value.toString() },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              isDefault={option.isDefault}
            />
          ))}
        </Row>
        <Box sx={{ minHeight: "16px", height: "fit-content", opacity: 0, pointerEvents: "none" }}>
          <Typography variant="body-sm" color={theme.palette.text["system-red"]}>
            To ensure the transaction is successful please set the priority fee higher than the
            validator tip.
          </Typography>
        </Box>
        <Box sx={{ marginTop: "16px" }} />

        {/* Validator Tip */}
        {showValidatorTip ? (
          <>
            <Row type="sc" fullWidth>
              <Typography variant="mobile-body-m-heavy">Validator Tip</Typography>
            </Row>
            <Row sx={{ marginTop: "8px" }} fullWidth>
              <Row sx={{ position: "relative" }} fullWidth>
                <TransparentInputWithUnit
                  type="number"
                  min={MIN_VALIDATOR_TIP}
                  max={MAX_VALIDATOR_TIP}
                  step="0.001"
                  amountRef={validatorTipRef}
                  onInputChange={handleValidatorTipChange}
                  defaultValue={validatorTip}
                  amount={validatorTip}
                  unit="SOL"
                />
              </Row>
            </Row>
            <Row type="sc" sx={{ gap: "8px", marginTop: "8px" }} fullWidth>
              {VALIDATOR_TIP_OPTIONS.map((option) => (
                <OptionButton
                  key={option.value}
                  label={option.label}
                  onClick={() => {
                    validatorTipRef.current!.value = option.value.toString();
                    handleValidatorTipChange({
                      target: { value: option.value.toString() },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  isDefault={option.isDefault}
                />
              ))}
            </Row>
            <Box
              sx={{
                minHeight: "16px",
                height: "fit-content",
                opacity: isShowTips ? 1 : 0,
                pointerEvents: isShowTips ? "auto" : "none",
              }}>
              <Typography variant="body-sm" color={theme.palette.text["system-red"]}>
                To ensure the transaction is successful please set the priority fee higher than the
                validator tip.
              </Typography>
            </Box>
            <Box sx={{ marginTop: "16px" }} />
          </>
        ) : null}
        {/* Confirm */}
        <BasicButton
          variant="white"
          onClick={handleConfirmPreferences}
          sx={{ width: "100%", padding: "14.5px" }}
          disabled={!isSlippageValid /*  || !isValidatorTipValid || !isPriorityFeeValid */}>
          <Typography variant="mobile-body-s-heavy">Done</Typography>
        </BasicButton>
        <Box
          sx={{
            marginTop: "64px",
          }}
        />
      </Col>
    </Drawer>
  );
};

const OptionButton = ({
  label,
  onClick,
  isDefault,
}: {
  label: string;
  onClick: () => void;
  isDefault: boolean;
}) => {
  const theme = useTheme();
  return (
    // <Box
    //   sx={{
    //     display: "flex",
    //     justifyContent: "space-between",
    //     alignItems: "center",
    //     width: "100%"
    //   }}>
    <BasicButton
      variant="dark"
      sx={{
        border: `1px solid ${theme.palette.border.light}`,
        backgroundColor: "#313339",
        height: "45px",
        padding: "16px",
        flex: 1,
      }}
      onClick={onClick}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 12px",
        }}>
        <Typography
          variant="mobile-action-s-heavy"
          sx={{ display: "inline-block", whiteSpace: "nowrap", textTransform: "capitalize" }}>
          {label.toLowerCase()}
        </Typography>
      </Box>
    </BasicButton>
    // </Box>
  );
};

const InputWithUnit = ({
  amount,
  amountRef,
  onInputChange,
  unit,
}: {
  amount: number;
  amountRef: React.RefObject<HTMLInputElement>;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  unit: string;
}) => {
  const theme = useTheme();
  const measureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (amountRef.current && measureRef.current) {
      measureRef.current.style.font = getComputedStyle(amountRef.current).font;
      measureRef.current.textContent = amountRef.current.value || "0"; // Default to "0" if empty
      amountRef.current.style.width = `${measureRef.current.offsetWidth + 10}px`;
    }
  }, [amount]);

  return (
    <Row
      type="cc"
      onClick={() => {
        amountRef.current?.focus();
      }}
      sx={{
        border: `1px solid ${theme.palette.border.light}`,
        height: "45px",
        paddingY: "12px",
        borderRadius: "16px",
      }}
      fullWidth>
      <TransparentInput
        type="number"
        ref={amountRef}
        sx={{
          width: "fit-content",
          display: "inline-block",
          padding: "4px",
          minWidth: "0",
          boxSizing: "border-box",
          border: "none",
        }}
        placeholder="0"
        onChange={(event) => {
          onInputChange(event);
        }}
      />
      <span
        ref={measureRef}
        id="measure"
        style={{ visibility: "hidden", position: "absolute", whiteSpace: "pre" }}></span>
      <Typography variant="mobile-body-s-heavy" color="text.primary">
        {unit}
      </Typography>
    </Row>
  );
};

const TransparentInput = styled("input")(({ theme }) => ({
  all: "unset",
  backgroundColor: "transparent",
  border: `1px solid ${theme.palette.border.light}`,
  borderRadius: "16px",
  outline: "none",
  paddingY: "12px",
  width: "100%",
  height: "45px",
  textAlign: "center",
  ...theme.typography["mobile-body-s"],
  "&:focus, &:not(:placeholder-shown)": {
    opacity: "1",
    outline: "none",
  },
  "&::placeholder": {
    color: theme.palette.text.secondary,
    ...theme.typography["mobile-body-s"],
  },
  color: theme.palette.text.primary,
}));

export { PreferencesDrawer };
